import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Property, UnderwritingAnalysis, MarketResearch } from "@shared/schema";
import { isHiddenSectionEnabled } from "@shared/hidden-sections";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { RENOVATION_ITEM_LABELS } from "@shared/schema";
import { ChevronRight, ChevronDown, FileDown, Info, DollarSign, Loader2, Hammer, KeyRound, ExternalLink } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const MAX_ROOMS = 100;

function n(val: string | null | undefined): number {
  if (!val) return 0;
  const parsed = parseFloat(val.replace(/[^0-9.\-]/g, ""));
  return isNaN(parsed) ? 0 : parsed;
}

function fmt(val: number): string {
  return val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCurrency(val: number): string {
  return "$" + fmt(val);
}

function fmtPct(val: number): string {
  return val.toFixed(2) + "%";
}

interface RoomData {
  rent?: string;
  notes?: string;
  furnished?: boolean;
  privateEntrance?: boolean;
  privateBath?: boolean;
  additions?: string;
}

interface BathroomMetrics {
  privateBathrooms: number;
  sharedBathrooms: number;
  unassignedBathrooms: number;
  effectiveBathroomsForRatio: number;
  bedroomsPerBathroom: number | null;
}

function Section({ title, defaultOpen = true, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border rounded-lg mb-4" data-tour="underwriting-detail-inputs">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 text-left" data-testid={`section-trigger-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {open ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function Field({ label, value, onChange, onBlur, type = "text", disabled = false, testId }: {
  label: string; value: string; onChange: (val: string) => void; onBlur: () => void; type?: string; disabled?: boolean; testId: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        className="text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function ReadOnlyField({ label, value, testId, tooltip }: { label: string; value: string; testId: string; tooltip?: string }) {
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <label className="block text-xs font-medium text-gray-600">{label}</label>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex items-center cursor-help focus:outline-none">
                  <Info className="h-3.5 w-3.5 text-gray-400" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs whitespace-pre-line">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <Input value={value} disabled className="text-sm bg-gray-50 font-medium" data-testid={testId} />
    </div>
  );
}

type LocalFields = Record<string, string | number | boolean | RoomData[] | null | undefined>;

export default function AppUnderwritingDetail() {
  const queryClient = useQueryClient();
  const [, params] = useRoute("/app/underwriting/:id");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const propertyId = params?.id ? parseInt(params.id) : 0;
  const [localFields, setLocalFields] = useState<LocalFields>({});
  const initializedRef = useRef(false);
  const [showMoveToRenovations, setShowMoveToRenovations] = useState(false);
  const [moveStartDate, setMoveStartDate] = useState("");
  const [movePermits, setMovePermits] = useState(false);
  const [movePermitDays, setMovePermitDays] = useState(45);
  const [movingInProgress, setMovingInProgress] = useState(false);
  const [transferItems, setTransferItems] = useState<{ key: string; itemType: string; itemName: string; budget: number; checked: boolean }[]>([]);
  const [showBedroomCalc, setShowBedroomCalc] = useState(false);
  const [calcSqft, setCalcSqft] = useState("");
  const [calcGarageSqft, setCalcGarageSqft] = useState("");
  const [calcNoGarage, setCalcNoGarage] = useState(false);
  const [calcError, setCalcError] = useState("");
  const [cityRatesFilling, setCityRatesFilling] = useState(false);

  const propertyQuery = useQuery<Property>({
    queryKey: ["/api/app/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!propertyId,
  });
  usePageTitle(propertyQuery.data?.address || propertyQuery.data?.name || "Underwriting");

  const analysisQuery = useQuery<UnderwritingAnalysis & { bathroomMetrics?: BathroomMetrics | null }>({
    queryKey: ["/api/app/underwriting", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/underwriting/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const bathroomMetrics: BathroomMetrics | null = analysisQuery.data?.bathroomMetrics ?? null;

  // City and state for market-rate lookups. The properties table now has a
  // dedicated `city` column (populated on save and backfilled from the address);
  // prefer it and only fall back to splitting the free-text address for older
  // rows whose city hasn't been backfilled yet.
  const { parsedCity, parsedState } = useMemo(() => {
    const directCity = propertyQuery.data?.city?.trim() || null;
    const directState = propertyQuery.data?.state?.trim() || null;
    if (directCity) {
      return { parsedCity: directCity, parsedState: directState };
    }
    const addr = propertyQuery.data?.address ?? "";
    const parts = addr.split(",").map((s) => s.trim());
    if (parts.length >= 3) {
      const city = parts[1] || null;
      // State is the first token of parts[2] (e.g. "GA 30301" → "GA")
      const state = (parts[2] || "").split(/\s+/)[0] || null;
      return { parsedCity: city, parsedState: directState ?? state };
    }
    return { parsedCity: null, parsedState: directState };
  }, [propertyQuery.data]);

  const cityRatesQuery = useQuery<MarketResearch[]>({
    queryKey: ["/api/app/market-research/city", parsedCity],
    queryFn: async () => {
      const res = await fetch(`/api/app/market-research/city/${encodeURIComponent(parsedCity!)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load city rates");
      return res.json();
    },
    enabled: !!parsedCity,
    staleTime: 5 * 60 * 1000,
  });

  // Pick the best matching row: prefer state match, fall back to first result
  const cityRatesRow = useMemo<MarketResearch | null>(() => {
    const rows = cityRatesQuery.data;
    if (!rows || rows.length === 0) return null;
    if (parsedState) {
      const stateMatch = rows.find((r) => (r.state || "").toLowerCase() === parsedState.toLowerCase());
      if (stateMatch) return stateMatch;
    }
    return rows[0];
  }, [cityRatesQuery.data, parsedState]);

  const hasCityRates = !!cityRatesRow && (!!cityRatesRow.avgPriceShared || !!cityRatesRow.avgPricePrivate);

  useEffect(() => {
    if (cityRatesQuery.isError && parsedCity) {
      toast({
        title: "Couldn't load city rates",
        description: `Failed to fetch market rate data for ${parsedCity}. Check your connection and try refreshing, or enter rates manually from the insights link.`,
        variant: "destructive",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityRatesQuery.isError]);

  useEffect(() => {
    if (analysisQuery.data && !initializedRef.current) {
      const a = analysisQuery.data;
      setLocalFields({
        zipCode: a.zipCode || "",
        currentBedrooms: a.currentBedrooms || 0,
        currentBathrooms: a.currentBathrooms || 0,
        futureBedrooms: a.futureBedrooms || 0,
        futureBathrooms: a.futureBathrooms || 0,
        privateBaths: a.privateBaths || 0,
        listingLink: a.listingLink || "",
        neighborhoodRating: a.neighborhoodRating || "",
        floorPlanLink: a.floorPlanLink || "",
        propertyReportLink: a.propertyReportLink || "",
        walkthroughVideoLink: a.walkthroughVideoLink || "",
        gdriveFolderLink: a.gdriveFolderLink || "",
        sqft: a.sqft || 0,
        garageSqft: a.garageSqft || 0,
        noGarage: a.noGarage || false,
        propertyNotes: a.propertyNotes || "",
        roomAdditionsQty: a.roomAdditionsQty || 0,
        roomAdditionsCost: a.roomAdditionsCost || "",
        bathAdditionsQty: a.bathAdditionsQty || 0,
        bathAdditionsCost: a.bathAdditionsCost || "",
        additionalRehabAmount: a.additionalRehabAmount || "",
        bufferPercent: a.bufferPercent || "",
        kitchenQty: a.kitchenQty || 0,
        kitchenAmount: a.kitchenAmount || "",
        hvacQty: a.hvacQty || 0,
        hvacAmount: a.hvacAmount || "",
        roofQty: a.roofQty || 0,
        roofAmount: a.roofAmount || "",
        plumbingQty: a.plumbingQty || 0,
        plumbingAmount: a.plumbingAmount || "",
        foundationRepairQty: a.foundationRepairQty || 0,
        foundationRepairAmount: a.foundationRepairAmount || "",
        otherQty: a.otherQty || 0,
        otherAmount: a.otherAmount || "",
        homePurchasePrice: a.homePurchasePrice || "",
        downpaymentPercent: a.downpaymentPercent || "",
        closingCostsPercent: a.closingCostsPercent || "",
        furnishings: a.furnishings || "",
        tempHoldingCost: a.tempHoldingCost || "",
        dealSponsorFee: a.dealSponsorFee || "",
        invoiceAuthorization: a.invoiceAuthorization || "",
        monthsUntilOccupied: a.monthsUntilOccupied || 0,
        annualInterestRate: a.annualInterestRate || "",
        loanTermYears: a.loanTermYears || 30,
        vacancyPercent: a.vacancyPercent || "",
        landValue: a.landValue || "",
        annualAppreciationAvg: a.annualAppreciationAvg || "",
        padsplitManagementFeePercent: a.padsplitManagementFeePercent || "8",
        pmFeePercent: a.pmFeePercent || "7",
        pmFeeType: a.pmFeeType || "percent",
        padsplitZipLink: a.padsplitZipLink || "",
        furnishedSharedWeekly: a.furnishedSharedWeekly || "",
        furnishedSharedMonthly: a.furnishedSharedMonthly || "",
        furnishedPrivateWeekly: a.furnishedPrivateWeekly || "",
        furnishedPrivateMonthly: a.furnishedPrivateMonthly || "",
        roomsData: (a.roomsData as RoomData[] | null) || [],
        sewerWater: a.sewerWater || "",
        gas: a.gas || "",
        electricity: a.electricity || "",
        internet: a.internet || "",
        lawnCare: a.lawnCare || "",
        cleaning: a.cleaning || "",
        capitalImprovementsBudget: a.capitalImprovementsBudget || "",
        miscRepairs: a.miscRepairs || "",
        hoa: a.hoa || "",
        annualPropertyTax: a.annualPropertyTax || "",
        annualHomeownersInsurance: a.annualHomeownersInsurance || "",
        annualPropertyTaxPercent: a.annualPropertyTaxPercent || "",
        annualInsurancePercent: a.annualInsurancePercent || "",
        landValuePercent: a.landValuePercent || "",
      });
      initializedRef.current = true;
    }
  }, [analysisQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("PATCH", `/api/app/underwriting/${propertyId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting", propertyId] });
    },
    onError: (error: Error) => {
      if (error.message.includes("401")) {
        toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
        navigate("/app/login");
      } else {
        toast({ title: "Failed to save", description: "Please try again.", variant: "destructive" });
      }
    },
  });

  const [rentData, setRentData] = useState<{ rentEstimateLow: string; rentEstimateHigh: string; rentEstimateMedian: string; ownerName: string } | null>(null);

  const rentEstimateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (input: string | { address: string; silent?: boolean }) => {
      const address = typeof input === "string" ? input : input.address;
      const silent = typeof input === "string" ? false : !!input.silent;
      const res = await apiRequest("POST", "/api/app/rent-estimate", { address });
      const data = await res.json();
      return { data, silent };
    },
    onSuccess: async ({ data, silent }) => {
      setRentData(data);
      const updates: Record<string, unknown> = {};
      updates.rentEstimateLow = data.rentEstimateLow || "";
      updates.rentEstimateHigh = data.rentEstimateHigh || "";
      updates.rentEstimateMedian = data.rentEstimateMedian || "";
      updates.ownerName = data.ownerName || "";
      const gotAny = !!(data.rentEstimateLow || data.rentEstimateHigh || data.rentEstimateMedian);
      try {
        await apiRequest("PATCH", `/api/app/underwriting/${propertyId}`, updates);
        queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting", propertyId] });
        // Suppress the "loaded" toast on the silent auto-fetch path so
        // users opening every fresh Underwriting record aren't spammed,
        // and never claim success when the provider returned no data.
        if (gotAny && !silent) {
          toast({ title: "Rent estimates loaded", description: "Data has been saved to your underwriting analysis." });
        }
      } catch {
        if (!silent) {
          toast({ title: "Rent data fetched but failed to save", description: "The data is shown but could not be saved to the database.", variant: "destructive" });
        }
      }
    },
    onError: (error: Error, input) => {
      const silent = typeof input === "object" && input !== null && !!input.silent;
      if (silent) return;
      toast({ title: "Failed to fetch rent estimates", description: parseErrorMessage(error, "Couldn't fetch rent estimates. Please try again."), variant: "destructive" });
    },
  });

  useEffect(() => {
    if (analysisQuery.data) {
      const a = analysisQuery.data as any;
      if (a.rentEstimateLow || a.rentEstimateHigh || a.rentEstimateMedian || a.ownerName) {
        setRentData({
          rentEstimateLow: a.rentEstimateLow || "",
          rentEstimateHigh: a.rentEstimateHigh || "",
          rentEstimateMedian: a.rentEstimateMedian || "",
          ownerName: a.ownerName || "",
        });
      }
    }
  }, [analysisQuery.data]);

  // Auto-pull rent estimates the first time an Underwriting record is
  // opened with an address but no saved rent figures yet. Keeps the
  // Buy Box → Underwriting handoff (and any new property created from a
  // Zillow import) from leaving the host with an empty rent section that
  // they'd otherwise have to click "Pull rent estimates" to populate.
  const autoRentTriedRef = useRef(false);
  useEffect(() => {
    if (autoRentTriedRef.current) return;
    if (!analysisQuery.data || !propertyQuery.data) return;
    const a = analysisQuery.data as any;
    const address = propertyQuery.data.address;
    if (!address) return;
    const hasRent = !!(a.rentEstimateLow || a.rentEstimateHigh || a.rentEstimateMedian);
    if (hasRent) return;
    if (rentEstimateMutation.isPending) return;
    autoRentTriedRef.current = true;
    rentEstimateMutation.mutate({ address, silent: true });
  }, [analysisQuery.data, propertyQuery.data, rentEstimateMutation]);

  const autoMoveTriedRef = useRef(false);
  useEffect(() => {
    if (autoMoveTriedRef.current) return;
    if (!analysisQuery.data || !propertyQuery.data) return;
    const search = new URLSearchParams(window.location.search);
    if (search.get("moveToRenovations") !== "1") return;
    autoMoveTriedRef.current = true;
    openMoveToRenovations();
    search.delete("moveToRenovations");
    const qs = search.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisQuery.data, propertyQuery.data]);

  const f = useCallback((field: string): string => {
    const v = localFields[field];
    if (v === undefined || v === null) return "";
    return String(v);
  }, [localFields]);

  const setField = useCallback((field: string, value: string | number | boolean) => {
    setLocalFields((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveField = useCallback((field: string) => {
    const value = localFields[field];
    updateMutation.mutate({ [field]: value });
  }, [localFields, updateMutation]);

  function calcBedroomsFromSqft(sqft: number, garageSqft: number): number {
    const total = sqft + garageSqft;
    const raw = total / 250;
    const floor = Math.floor(raw);
    const decimal = raw - floor;
    return decimal >= 0.5 ? Math.ceil(raw) : floor;
  }

  function calcBathroomsFromBedrooms(bedrooms: number): number {
    return Math.ceil(bedrooms / 3);
  }

  function handleBedroomCalcClick() {
    const currentSqft = Number(localFields.sqft) || 0;
    if (currentSqft > 0) {
      const garageSqft = Number(localFields.noGarage) ? 0 : (Number(localFields.garageSqft) || 0);
      const bedrooms = calcBedroomsFromSqft(currentSqft, garageSqft);
      const newBed = Math.min(bedrooms, MAX_ROOMS);
      const newBath = bathroomMetrics && bathroomMetrics.effectiveBathroomsForRatio > 0
        ? bathroomMetrics.effectiveBathroomsForRatio
        : calcBathroomsFromBedrooms(newBed);
      setLocalFields((prev) => ({ ...prev, futureBedrooms: newBed, futureBathrooms: newBath }));
      updateMutation.mutate({ futureBedrooms: newBed, futureBathrooms: newBath });
    } else {
      setCalcSqft("");
      setCalcGarageSqft("");
      setCalcNoGarage(false);
      setCalcError("");
      setShowBedroomCalc(true);
    }
  }

  function handleBedroomCalcSubmit() {
    const garageSqftVal = parseInt(calcGarageSqft) || 0;
    if (calcNoGarage && garageSqftVal > 0) {
      setCalcError("You can't have garage square footage and no garage selected. Please uncheck 'No garage available' or clear the garage square footage.");
      return;
    }
    if (!calcNoGarage && garageSqftVal <= 0) {
      setCalcError("Please enter garage square footage or check 'No garage available'.");
      return;
    }
    setCalcError("");
    const sqft = parseInt(calcSqft) || 0;
    const garageSqft = calcNoGarage ? 0 : garageSqftVal;
    const bedrooms = calcBedroomsFromSqft(sqft, garageSqft);
    const newBed = Math.min(bedrooms, MAX_ROOMS);
    const newBath = bathroomMetrics && bathroomMetrics.effectiveBathroomsForRatio > 0
      ? bathroomMetrics.effectiveBathroomsForRatio
      : calcBathroomsFromBedrooms(newBed);
    setLocalFields((prev) => ({ ...prev, sqft, garageSqft, noGarage: calcNoGarage, futureBedrooms: newBed, futureBathrooms: newBath }));
    updateMutation.mutate({ sqft, garageSqft, noGarage: calcNoGarage, futureBedrooms: newBed, futureBathrooms: newBath });
    setShowBedroomCalc(false);
  }

  const roomsData = (localFields.roomsData as RoomData[] | null) || [];

  const setRoomField = useCallback((index: number, field: keyof RoomData, value: string | boolean) => {
    setLocalFields((prev) => {
      const current = (prev.roomsData as RoomData[] | null) || [];
      const updated = [...current];
      while (updated.length <= index) updated.push({});
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, roomsData: updated };
    });
  }, []);

  const saveRoomsData = useCallback(() => {
    updateMutation.mutate({ roomsData: localFields.roomsData });
  }, [localFields.roomsData, updateMutation]);

  const calculations = useMemo(() => {
    const currentBed = Number(localFields.currentBedrooms) || 0;
    const futureBed = Number(localFields.futureBedrooms) || 0;
    const currentBath = Number(localFields.currentBathrooms) || 0;
    const futureBath = Number(localFields.futureBathrooms) || 0;
    const roomAddQty = Math.max(0, futureBed - currentBed);
    const roomAddCost = n(f("roomAdditionsCost"));
    const effectiveBath = bathroomMetrics && bathroomMetrics.effectiveBathroomsForRatio > 0
      ? bathroomMetrics.effectiveBathroomsForRatio
      : futureBath;
    const bathAddQty = Math.max(0, effectiveBath - currentBath);
    const bedsPerBath = effectiveBath > 0 ? futureBed / effectiveBath : null;
    const bathAddCost = n(f("bathAdditionsCost"));
    const kitchenQty = Number(localFields.kitchenQty) || 0;
    const kitchenAmt = n(f("kitchenAmount"));
    const hvacQty = Number(localFields.hvacQty) || 0;
    const hvacAmt = n(f("hvacAmount"));
    const roofQty = Number(localFields.roofQty) || 0;
    const roofAmt = n(f("roofAmount"));
    const plumbingQty = Number(localFields.plumbingQty) || 0;
    const plumbingAmt = n(f("plumbingAmount"));
    const foundationQty = Number(localFields.foundationRepairQty) || 0;
    const foundationAmt = n(f("foundationRepairAmount"));
    const otherQty = Number(localFields.otherQty) || 0;
    const otherAmt = n(f("otherAmount"));

    const roomAdditionsTotal = roomAddQty * roomAddCost;
    const bathAdditionsTotal = bathAddQty * bathAddCost;
    const kitchenTotal = kitchenQty * kitchenAmt;
    const hvacTotal = hvacQty * hvacAmt;
    const roofTotal = roofQty * roofAmt;
    const plumbingTotal = plumbingQty * plumbingAmt;
    const foundationTotal = foundationQty * foundationAmt;
    const otherTotal = otherQty * otherAmt;
    const purchasePriceForRehab = n(f("homePurchasePrice"));
    const additionalRehabPct = n(f("additionalRehabAmount")) / 100;
    const additionalRehab = purchasePriceForRehab * additionalRehabPct;
    const subtotalCapex = roomAdditionsTotal + bathAdditionsTotal + kitchenTotal + hvacTotal + roofTotal + plumbingTotal + foundationTotal + otherTotal + additionalRehab;
    const bufferPct = n(f("bufferPercent")) / 100;
    const totalCapex = subtotalCapex * (1 + bufferPct);

    const purchasePrice = n(f("homePurchasePrice"));
    const downPct = n(f("downpaymentPercent")) / 100;
    const closingPct = n(f("closingCostsPercent")) / 100;
    const downpayment = purchasePrice * downPct;
    const closingCosts = purchasePrice * closingPct;
    const furnishingsVal = n(f("furnishings"));
    const dealSponsor = n(f("dealSponsorFee"));
    const invoiceAuth = n(f("invoiceAuthorization"));

    const loanAmount = purchasePrice - downpayment;
    const annualRate = n(f("annualInterestRate")) / 100;
    const monthlyRate = annualRate / 12;
    const loanTermMonths = (Number(localFields.loanTermYears) || 30) * 12;
    let mortgagePI = 0;
    if (monthlyRate > 0 && loanAmount > 0) {
      mortgagePI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) / (Math.pow(1 + monthlyRate, loanTermMonths) - 1);
    }

    const futureBedrooms = Math.min(Number(localFields.futureBedrooms) || 0, MAX_ROOMS);
    const rd = roomsData;
    let totalMonthlyIncome = 0;
    for (let i = 0; i < futureBedrooms; i++) {
      const room = rd[i] || {};
      totalMonthlyIncome += n(room.rent) + n(room.additions);
    }

    const vacancyPct = n(f("vacancyPercent")) / 100;
    const padsplitMgmtPct = n(f("padsplitManagementFeePercent")) / 100 || 0.08;
    const pmFeeTypeVal = String(localFields.pmFeeType || "percent");
    let pmFee: number;
    if (pmFeeTypeVal === "fixed") {
      pmFee = n(f("pmFeePercent"));
    } else {
      const pmFeePct = n(f("pmFeePercent")) / 100 || 0.07;
      pmFee = totalMonthlyIncome * pmFeePct;
    }

    const padsplitMgmtFee = totalMonthlyIncome * padsplitMgmtPct;
    const electricityCalc = 35 * futureBedrooms;

    const annualPropertyTaxPct = n(f("annualPropertyTaxPercent")) / 100;
    const annualInsurancePct = n(f("annualInsurancePercent")) / 100;
    const annualPropertyTaxCalc = annualPropertyTaxPct > 0 ? purchasePrice * annualPropertyTaxPct : n(f("annualPropertyTax"));
    const annualInsuranceCalc = annualInsurancePct > 0 ? purchasePrice * annualInsurancePct : n(f("annualHomeownersInsurance"));
    const monthlyPropertyTax = annualPropertyTaxCalc / 12;
    const monthlyInsurance = annualInsuranceCalc / 12;
    const vacancyAmount = totalMonthlyIncome * vacancyPct;

    const totalMonthlyExpenses =
      n(f("sewerWater")) + n(f("gas")) + electricityCalc + n(f("internet")) +
      n(f("lawnCare")) + n(f("cleaning")) + n(f("capitalImprovementsBudget")) +
      n(f("miscRepairs")) + n(f("hoa")) + monthlyPropertyTax + monthlyInsurance +
      mortgagePI + padsplitMgmtFee + pmFee + vacancyAmount;

    const monthsUntilOcc = Number(localFields.monthsUntilOccupied) || 0;
    const monthlyHoldingCosts = mortgagePI + monthlyPropertyTax + monthlyInsurance + n(f("sewerWater")) + n(f("gas")) + electricityCalc + n(f("internet")) + n(f("hoa"));
    const tempHolding = monthsUntilOcc > 0
      ? monthlyHoldingCosts * monthsUntilOcc
      : n(f("tempHoldingCost"));
    const totalCashInvested = downpayment + closingCosts + furnishingsVal + totalCapex + tempHolding + dealSponsor + invoiceAuth;

    const monthlyCashflow = totalMonthlyIncome - totalMonthlyExpenses;
    const annualCashflow = monthlyCashflow * 12;
    const cashOnCash = totalCashInvested > 0 ? (annualCashflow / totalCashInvested) * 100 : 0;
    const costPerRoom = futureBedrooms > 0 ? totalCashInvested / futureBedrooms : 0;

    const landValuePct = n(f("landValuePercent")) / 100;
    const landValueCalc = landValuePct > 0 ? purchasePrice * landValuePct : n(f("landValue"));
    const buildingValue = purchasePrice - landValueCalc;
    const depreciableBasis = buildingValue + totalCapex;
    const annualDepreciation = depreciableBasis / 27.5;
    const costSegYear1 = depreciableBasis * 0.30;
    const costSegRemaining = (depreciableBasis - costSegYear1) / 27.5;

    const annualNOI = (totalMonthlyIncome - totalMonthlyExpenses + mortgagePI) * 12;
    const monthlyNOI = annualNOI / 12;
    const capRate6 = annualNOI > 0 ? annualNOI / 0.06 : 0;
    const capRate7 = annualNOI > 0 ? annualNOI / 0.07 : 0;
    const capRate8 = annualNOI > 0 ? annualNOI / 0.08 : 0;
    const capRate10 = annualNOI > 0 ? annualNOI / 0.10 : 0;

    return {
      roomAddQty, bathAddQty, additionalRehab,
      roomAdditionsTotal, bathAdditionsTotal, kitchenTotal, hvacTotal, roofTotal,
      plumbingTotal, foundationTotal, otherTotal, subtotalCapex, totalCapex,
      downpayment, closingCosts, totalCashInvested, tempHolding,
      mortgagePI, totalMonthlyIncome, totalMonthlyExpenses,
      padsplitMgmtFee, pmFee, electricityCalc, monthlyPropertyTax, monthlyInsurance, vacancyAmount,
      monthlyCashflow, cashOnCash, costPerRoom,
      depreciableBasis, annualDepreciation, costSegYear1, costSegRemaining,
      monthlyNOI, capRate6, capRate7, capRate8, capRate10,
      loanAmount, purchasePrice, futureBedrooms,
      annualPropertyTaxCalc, annualInsuranceCalc, landValueCalc,
      buildingValue, annualNOI, monthsUntilOcc,
      effectiveBath, bedsPerBath,
    };
  }, [localFields, roomsData, f, bathroomMetrics]);

  const property = propertyQuery.data;

  const handleExportPDF = useCallback(async () => {
    if (!property || !calculations) return;
    const jsPDFModule = await import("jspdf");
    const jsPDF = jsPDFModule.default;
    const { default: autoTable } = await import("jspdf-autotable");

    const doc = new jsPDF();
    type jsPDFWithAutoTable = typeof doc & { lastAutoTable: { finalY: number } };
    const primaryBlue = [0, 82, 204] as [number, number, number];
    const accentOrange = [255, 165, 0] as [number, number, number];
    const pageWidth = doc.internal.pageSize.getWidth();
    const exportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    doc.setFillColor(...primaryBlue);
    doc.rect(0, 0, pageWidth, 35, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("PadPulse", 14, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Underwriting Analysis", 14, 26);
    doc.setFontSize(10);
    doc.text(exportDate, pageWidth - 14, 18, { align: "right" });
    doc.text(property.address || property.name, pageWidth - 14, 26, { align: "right" });

    let y = 45;
    const addSectionHeader = (title: string) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFillColor(...accentOrange);
      doc.rect(14, y, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(title, 17, y + 5.5);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      y += 12;
    };

    const addTable = (rows: [string, string][]) => {
      if (y > 240) { doc.addPage(); y = 20; }
      autoTable(doc, {
        startY: y,
        head: [],
        body: rows,
        theme: "striped",
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 80 }, 1: { halign: "right" } },
        margin: { left: 14, right: 14 },
      });
      y = (doc as jsPDFWithAutoTable).lastAutoTable.finalY + 6;
    };

    addSectionHeader("Property Details");
    addTable([
      ["Address", property.address || "—"],
      ["Zip Code", f("zipCode") || "—"],
      ["Current Bed / Bath", `${f("currentBedrooms") || 0} / ${f("currentBathrooms") || 0}`],
      ["Future Bed / Bath", `${calculations.futureBedrooms} / ${f("futureBathrooms") || 0}`],
      ["Private Baths", f("privateBaths") || "0"],
      ...(bathroomMetrics ? [["Beds/Bath Ratio (rooms)", calculations.bedsPerBath !== null ? calculations.bedsPerBath.toFixed(2) : "—"] as [string, string]] : []),
      ["SQFT", f("sqft") || "—"],
      ["Garage SQFT", f("garageSqft") || "—"],
    ]);

    addSectionHeader("Cash Invested");
    addTable([
      ["Home Purchase Price", fmtCurrency(calculations.purchasePrice)],
      ["Downpayment", fmtCurrency(calculations.downpayment)],
      ["Closing Costs", fmtCurrency(calculations.closingCosts)],
      ["Furnishings", fmtCurrency(n(f("furnishings")))],
      ["Improvements / CapEx", fmtCurrency(calculations.totalCapex)],
      ["Temp Holding Cost", fmtCurrency(calculations.tempHolding)],
      ["Deal Sponsor Fee", fmtCurrency(n(f("dealSponsorFee")))],
      ["Invoice Authorization", fmtCurrency(n(f("invoiceAuthorization")))],
      ["Total Cash Invested", fmtCurrency(calculations.totalCashInvested)],
    ]);

    addSectionHeader("Remodel / CapEx Breakdown");
    addTable([
      ["Room Additions", fmtCurrency(calculations.roomAdditionsTotal)],
      ["Bath Additions", fmtCurrency(calculations.bathAdditionsTotal)],
      ["Kitchen", fmtCurrency(calculations.kitchenTotal)],
      ["HVAC", fmtCurrency(calculations.hvacTotal)],
      ["Roof", fmtCurrency(calculations.roofTotal)],
      ["Plumbing", fmtCurrency(calculations.plumbingTotal)],
      ["Foundation Repair", fmtCurrency(calculations.foundationTotal)],
      ["Other", fmtCurrency(calculations.otherTotal)],
      [`Additional Rehab (${f("additionalRehabAmount") || 0}% of PP)`, fmtCurrency(calculations.additionalRehab)],
      ["Subtotal", fmtCurrency(calculations.subtotalCapex)],
      [`Buffer (${f("bufferPercent") || 0}%)`, fmtCurrency(calculations.totalCapex - calculations.subtotalCapex)],
      ["Total CapEx", fmtCurrency(calculations.totalCapex)],
    ]);

    addSectionHeader("Monthly Income (Room-by-Room)");
    const incomeRows: [string, string][] = [];
    for (let i = 0; i < calculations.futureBedrooms; i++) {
      const room = roomsData[i] || {};
      const total = n(room.rent) + n(room.additions);
      incomeRows.push([`Room ${i + 1}${room.notes ? ` (${room.notes})` : ""}`, fmtCurrency(total)]);
    }
    incomeRows.push(["Total Monthly Gross Income", fmtCurrency(calculations.totalMonthlyIncome)]);
    addTable(incomeRows);

    addSectionHeader("Monthly Expenses");
    addTable([
      ["Sewer & Water", fmtCurrency(n(f("sewerWater")))],
      ["Gas", fmtCurrency(n(f("gas")))],
      ["Electricity", fmtCurrency(calculations.electricityCalc)],
      ["Internet", fmtCurrency(n(f("internet")))],
      ["Lawn Care", fmtCurrency(n(f("lawnCare")))],
      ["Cleaning", fmtCurrency(n(f("cleaning")))],
      ["Capital Improvements", fmtCurrency(n(f("capitalImprovementsBudget")))],
      ["Misc Repairs", fmtCurrency(n(f("miscRepairs")))],
      ["HOA", fmtCurrency(n(f("hoa")))],
      [`Property Tax (${fmtCurrency(calculations.annualPropertyTaxCalc)}/yr)`, fmtCurrency(calculations.monthlyPropertyTax)],
      [`Insurance (${fmtCurrency(calculations.annualInsuranceCalc)}/yr)`, fmtCurrency(calculations.monthlyInsurance)],
      ["Mortgage P&I", fmtCurrency(calculations.mortgagePI)],
      ["PadSplit Mgmt Fee", fmtCurrency(calculations.padsplitMgmtFee)],
      ["PM Fee", fmtCurrency(calculations.pmFee)],
      ["Vacancy", fmtCurrency(calculations.vacancyAmount)],
      ["Total Monthly Expenses", fmtCurrency(calculations.totalMonthlyExpenses)],
    ]);

    addSectionHeader("Key Metrics");
    addTable([
      ["Cost Per Room", fmtCurrency(calculations.costPerRoom)],
      ["Cash-on-Cash Return", fmtPct(calculations.cashOnCash)],
      ["Monthly Cashflow", fmtCurrency(calculations.monthlyCashflow)],
      ["Annual Cashflow", fmtCurrency(calculations.monthlyCashflow * 12)],
    ]);

    addSectionHeader("Depreciation Schedule");
    addTable([
      ["Annual Depreciation (27.5yr)", fmtCurrency(calculations.annualDepreciation)],
      ["Cost Seg Year 1 (30% @ 100%)", fmtCurrency(calculations.costSegYear1)],
      ["Cost Seg Remaining Annual", fmtCurrency(calculations.costSegRemaining)],
    ]);

    addSectionHeader("CAP Rate Calculator");
    addTable([
      ["Monthly NOI", fmtCurrency(calculations.monthlyNOI)],
      ["Value @ 6 CAP", fmtCurrency(calculations.capRate6)],
      ["Value @ 7 CAP", fmtCurrency(calculations.capRate7)],
      ["Value @ 8 CAP", fmtCurrency(calculations.capRate8)],
      ["Value @ 10 CAP", fmtCurrency(calculations.capRate10)],
    ]);

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Generated by PadPulse | ${exportDate}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
    }

    const fileName = `${(property.address || property.name).replace(/[^a-zA-Z0-9]/g, "_")}_Underwriting.pdf`;
    doc.save(fileName);
    toast({ title: "PDF exported successfully" });
  }, [property, calculations, roomsData, f, toast]);

  if (propertyQuery.isLoading || analysisQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!property || !analysisQuery.data) {
    return (
      <AppLayout>
        <div className="p-6 text-center py-20">
          <p className="text-gray-500">Analysis not found</p>
          <Button variant="ghost" asChild data-testid="link-back-underwriting">
            <Link href="/app/underwriting">Back to Underwriting</Link>
          </Button>
        </div>
      </AppLayout>
    );
  }

  const futureBedrooms = calculations.futureBedrooms;

  interface TransferItem {
    key: string;
    itemType: string;
    itemName: string;
    budget: number;
    checked: boolean;
  }

  function buildTransferItems() {
    const items: TransferItem[] = [];
    const c = calculations;

    if (c.roomAdditionsTotal > 0) items.push({ key: "bedrooms", itemType: "bedrooms", itemName: RENOVATION_ITEM_LABELS.bedrooms || "Bedrooms", budget: c.roomAdditionsTotal, checked: true });
    if (c.bathAdditionsTotal > 0) items.push({ key: "bathroom_remodel", itemType: "bathroom_remodel", itemName: RENOVATION_ITEM_LABELS.bathroom_remodel || "Bathroom Remodel", budget: c.bathAdditionsTotal, checked: true });
    if (c.kitchenTotal > 0) items.push({ key: "kitchen_remodel", itemType: "kitchen_remodel", itemName: RENOVATION_ITEM_LABELS.kitchen_remodel || "Kitchen Remodel", budget: c.kitchenTotal, checked: true });
    if (c.hvacTotal > 0) items.push({ key: "hvac", itemType: "hvac", itemName: RENOVATION_ITEM_LABELS.hvac || "HVAC", budget: c.hvacTotal, checked: true });
    if (c.roofTotal > 0) items.push({ key: "roof_replacement", itemType: "roof_replacement", itemName: RENOVATION_ITEM_LABELS.roof_replacement || "Roof Replacement", budget: c.roofTotal, checked: true });
    if (c.plumbingTotal > 0) items.push({ key: "plumbing", itemType: "plumbing", itemName: RENOVATION_ITEM_LABELS.plumbing || "Plumbing", budget: c.plumbingTotal, checked: true });
    if (c.foundationTotal > 0) items.push({ key: "foundation", itemType: "foundation", itemName: RENOVATION_ITEM_LABELS.foundation || "Foundation", budget: c.foundationTotal, checked: true });
    if (c.otherTotal > 0) items.push({ key: "general_demolition", itemType: "general_demolition", itemName: "Other / General", budget: c.otherTotal, checked: true });
    if (c.additionalRehab > 0) items.push({ key: "additional_rehab", itemType: "painting", itemName: "Additional Rehab", budget: c.additionalRehab, checked: true });

    const bufferPct = n(f("bufferPercent")) / 100;
    if (bufferPct > 0 && c.subtotalCapex > 0) {
      const bufferAmount = c.subtotalCapex * bufferPct;
      items.push({ key: "buffer", itemType: "building", itemName: `Buffer (${n(f("bufferPercent"))}%)`, budget: bufferAmount, checked: true });
    }

    return items;
  }

  function openMoveToRenovations() {
    setMoveStartDate("");
    setMovePermits(false);
    setMovePermitDays(45);
    setTransferItems(buildTransferItems());
    setShowMoveToRenovations(true);
  }

  async function handleStartAcquisition() {
    try {
      const res = await apiRequest("POST", `/api/app/acquisitions/from-property/${propertyId}`, {});
      const acquisition = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      toast({ title: "Acquisition started! Property moved to Acquisition status." });
      navigate(`/app/acquisitions/${acquisition.id}`);
    } catch {
      toast({ title: "Failed to start acquisition", variant: "destructive" });
    }
  }

  function toggleTransferItem(key: string) {
    setTransferItems((prev) => prev.map((it) => it.key === key ? { ...it, checked: !it.checked } : it));
  }

  function updateTransferBudget(key: string, budget: number) {
    setTransferItems((prev) => prev.map((it) => it.key === key ? { ...it, budget } : it));
  }

  async function handleMoveToRenovations() {
    if (!moveStartDate) {
      toast({ title: "Please set a renovation start date", variant: "destructive" });
      return;
    }
    setMovingInProgress(true);
    try {
      await apiRequest("PATCH", `/api/app/properties/${propertyId}`, {
        status: "renovations",
        renovationStatus: "not_started",
        renovationStartDate: moveStartDate,
        permitsRequired: movePermits,
        permitDays: movePermitDays,
      });

      const checkedItems = transferItems.filter((it) => it.checked);
      if (checkedItems.length > 0) {
        let defaultDays: Record<string, number> = {};
        try {
          const dRes = await fetch("/api/app/renovation-defaults", { credentials: "include" });
          if (dRes.ok) {
            const dData = await dRes.json();
            defaultDays = dData?.defaultDays || {};
          }
        } catch {}
        for (let i = 0; i < checkedItems.length; i++) {
          const it = checkedItems[i];
          await apiRequest("POST", `/api/app/properties/${propertyId}/renovation-items`, {
            itemType: it.itemType,
            itemName: it.itemName,
            estimatedDays: defaultDays[it.itemType] || 7,
            sortOrder: i,
            budget: it.budget > 0 ? String(Math.round(it.budget)) : null,
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId] });
      toast({ title: "Property moved to Renovations" });
      setShowMoveToRenovations(false);
      navigate(`/app/renovations/${propertyId}`);
    } catch {
      toast({ title: "Failed to move to renovations", variant: "destructive" });
    } finally {
      setMovingInProgress(false);
    }
  }

  const transferTotal = transferItems.filter((it) => it.checked).reduce((s, it) => s + it.budget, 0);

  return (
    <AppLayout>
      <div className="p-6 max-w-5xl">
        <ViewOnlyBanner area="underwriting" />
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link href="/app/underwriting">
            <span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-underwriting">Underwriting</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-900" data-testid="text-breadcrumb-current">{property.name}</span>
        </div>

        {analysisQuery.data?.sourceType && analysisQuery.data?.sourceListingId && (
          <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-800" data-testid="banner-source-attribution">
            <ExternalLink className="h-4 w-4 flex-shrink-0 text-blue-500" />
            <span>
              Sourced from{" "}
              <strong>
                {analysisQuery.data.sourceType === "marketplace" ? "Sell tab" : analysisQuery.data.sourceType === "buydeal" ? "Buy Deals" : "Buy tab"}
              </strong>
              {(property.address || property.name) && (
                <> — {property.address || property.name}</>
              )}
            </span>
            <button
              className="ml-auto text-blue-600 hover:text-blue-800 underline text-xs font-medium whitespace-nowrap"
              onClick={() => {
                const type = analysisQuery.data?.sourceType;
                const id = analysisQuery.data?.sourceListingId;
                if (type === "wholesaler") navigate(`/app/buy?highlight=${id}`);
                else if (type === "realtor") navigate(`/app/realtor-listings/${id}`);
                else if (type === "marketplace") navigate(`/app/sell/${id}`);
                else if (type === "buydeal") navigate(`/app/buy/${id}`);
              }}
              data-testid="link-source-listing"
            >
              View source listing
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-y-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-analysis-title" data-tour="underwriting-detail-heading">{property.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {/* B19 — Acquisitions and Renovations are switched off in shared/hidden-sections.ts. */}
            {isHiddenSectionEnabled("acquisitions") && (
              <Button onClick={handleStartAcquisition} variant="outline" className="border-green-600 text-green-700 hover:bg-green-50" data-testid="button-start-acquisition">
                <KeyRound className="h-4 w-4 mr-2" /> Start Acquisition
              </Button>
            )}
            {isHiddenSectionEnabled("renovations") && (
              <Button onClick={openMoveToRenovations} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="button-move-to-renovations">
                <Hammer className="h-4 w-4 mr-2" /> Move to Renovations
              </Button>
            )}
            <Button onClick={handleExportPDF} className="bg-brand hover:bg-brand-hover" data-testid="button-export-pdf">
              <FileDown className="h-4 w-4 mr-2" /> Export PDF
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="underwriting-detail" />
              <GuideButton pageKey="underwriting-detail" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="underwriting-detail" />
        </div>

        <Dialog open={showMoveToRenovations} onOpenChange={(v) => !v && setShowMoveToRenovations(false)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hammer className="h-5 w-5 text-amber-600" />
                Move to Renovations
              </DialogTitle>
              <DialogDescription>
                We've pulled your remodel and CapEx line items from underwriting. Set your renovation plan details and adjust budgets as needed.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Plan Start Date *</label>
                  <Input
                    type="date"
                    value={moveStartDate}
                    onChange={(e) => setMoveStartDate(e.target.value)}
                    data-testid="input-move-start-date"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2 mt-6">
                    <Switch
                      checked={movePermits}
                      onCheckedChange={setMovePermits}
                      data-testid="switch-move-permits"
                    />
                    <label className="text-sm font-semibold text-gray-700">Permits Required</label>
                  </div>
                  {movePermits && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        className="w-24 text-sm"
                        value={movePermitDays}
                        onChange={(e) => setMovePermitDays(parseInt(e.target.value) || 45)}
                        data-testid="input-move-permit-days"
                      />
                      <span className="text-xs text-gray-400">days</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">CapEx Line Items from Underwriting</h3>
                  <span className="text-sm font-semibold text-gray-900" data-testid="text-transfer-total">
                    Total: ${transferTotal.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-3">Uncheck items you don't want to transfer. Adjust budgets as needed.</p>

                <div className="border rounded-lg overflow-hidden" data-tour="underwriting-detail-summary">
                  <div className="grid grid-cols-[auto_1fr_120px] gap-x-3 items-center px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">
                    <div className="w-5"></div>
                    <div>Item</div>
                    <div className="text-right">Budget</div>
                  </div>
                  {transferItems.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-400">No CapEx items to transfer. All values are $0.</div>
                  ) : (
                    transferItems.map((item) => (
                      <div
                        key={item.key}
                        className={`grid grid-cols-[auto_1fr_120px] gap-x-3 items-center px-3 py-2.5 border-b last:border-b-0 transition-colors ${item.checked ? "bg-amber-50" : "bg-white"}`}
                      >
                        <div className="w-5">
                          <Checkbox
                            checked={item.checked}
                            onCheckedChange={() => toggleTransferItem(item.key)}
                            data-testid={`checkbox-transfer-${item.key}`}
                          />
                        </div>
                        <span className={`text-sm ${item.checked ? "text-gray-800" : "text-gray-400 line-through"}`}>{item.itemName}</span>
                        <Input
                          type="number"
                          min={0}
                          step="100"
                          className="w-28 h-7 text-xs px-1 text-right ml-auto"
                          value={Math.round(item.budget)}
                          onChange={(e) => updateTransferBudget(item.key, parseFloat(e.target.value) || 0)}
                          disabled={!item.checked}
                          data-testid={`input-transfer-budget-${item.key}`}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowMoveToRenovations(false)} data-testid="button-cancel-move">Cancel</Button>
              <Button onClick={handleMoveToRenovations} disabled={movingInProgress} data-testid="button-save-move">
                {movingInProgress ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Moving...</> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Section title="Property Details">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <ReadOnlyField label="Address" value={property.address || ""} testId="input-uw-address" />
            <Field label="Zip Code" value={f("zipCode")} onChange={(v) => setField("zipCode", v)} onBlur={() => saveField("zipCode")} testId="input-uw-zipcode" />
            <Field label="Current Bedrooms" value={f("currentBedrooms")} onChange={(v) => setField("currentBedrooms", parseInt(v) || 0)} onBlur={() => saveField("currentBedrooms")} type="number" testId="input-uw-current-bed" />
            <Field label="Current Bathrooms" value={f("currentBathrooms")} onChange={(v) => setField("currentBathrooms", parseInt(v) || 0)} onBlur={() => saveField("currentBathrooms")} type="number" testId="input-uw-current-bath" />
            <Field label="Private Baths" value={f("privateBaths")} onChange={(v) => setField("privateBaths", parseInt(v) || 0)} onBlur={() => saveField("privateBaths")} type="number" testId="input-uw-private-baths" />
            <div className="flex flex-col justify-center">
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-9 mt-5"
                onClick={handleBedroomCalcClick}
                data-testid="button-calc-future-bedrooms"
              >
                Calculate future bedrooms
              </Button>
            </div>
            <Field label="Future Bedrooms" value={f("futureBedrooms")} onChange={(v) => setField("futureBedrooms", Math.min(parseInt(v) || 0, MAX_ROOMS))} onBlur={() => saveField("futureBedrooms")} type="number" testId="input-uw-future-bed" />
            <Field label="Future Bathrooms" value={f("futureBathrooms")} onChange={(v) => setField("futureBathrooms", parseInt(v) || 0)} onBlur={() => saveField("futureBathrooms")} type="number" testId="input-uw-future-bath" />
            {bathroomMetrics && (
              <ReadOnlyField
                label="Beds/Bath Ratio (from rooms)"
                value={calculations.bedsPerBath !== null ? calculations.bedsPerBath.toFixed(2) : "—"}
                testId="input-uw-beds-per-bath"
                tooltip={`Derived from room associations: ${bathroomMetrics.privateBathrooms} private, ${bathroomMetrics.sharedBathrooms} shared, ${bathroomMetrics.unassignedBathrooms} unassigned bathroom(s). Effective bathrooms used: ${bathroomMetrics.effectiveBathroomsForRatio}.`}
              />
            )}
          </div>
        </Section>

        <Dialog open={showBedroomCalc} onOpenChange={(v) => !v && setShowBedroomCalc(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Calculate Future Bedrooms</DialogTitle>
              <DialogDescription>
                Enter the square footage to calculate the number of future bedrooms. Formula: (SQFT + Garage SQFT) / 250.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Square Footage</label>
                <Input
                  type="number"
                  min={0}
                  value={calcSqft}
                  onChange={(e) => setCalcSqft(e.target.value)}
                  placeholder="e.g. 1500"
                  data-testid="input-calc-sqft"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Garage Square Footage</label>
                <Input
                  type="number"
                  min={0}
                  value={calcGarageSqft}
                  onChange={(e) => setCalcGarageSqft(e.target.value)}
                  placeholder="e.g. 400"
                  disabled={calcNoGarage}
                  data-testid="input-calc-garage-sqft"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="no-garage"
                  checked={calcNoGarage}
                  onCheckedChange={(checked) => {
                    setCalcNoGarage(!!checked);
                    setCalcError("");
                  }}
                  data-testid="checkbox-no-garage"
                />
                <label htmlFor="no-garage" className="text-sm text-gray-700 cursor-pointer">No garage available</label>
              </div>
              {calcError && (
                <p className="text-sm text-red-600 mt-1" data-testid="text-calc-error">{calcError}</p>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowBedroomCalc(false)} data-testid="button-cancel-calc">Cancel</Button>
              <Button onClick={handleBedroomCalcSubmit} data-testid="button-submit-calc">Calculate</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Section title="Property Details Extended">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Listing Link" value={f("listingLink")} onChange={(v) => setField("listingLink", v)} onBlur={() => saveField("listingLink")} testId="input-uw-listing-link" />
            <Field label="Floor Plan Link" value={f("floorPlanLink")} onChange={(v) => setField("floorPlanLink", v)} onBlur={() => saveField("floorPlanLink")} testId="input-uw-floor-plan" />
            <Field label="Walkthrough Video Link" value={f("walkthroughVideoLink")} onChange={(v) => setField("walkthroughVideoLink", v)} onBlur={() => saveField("walkthroughVideoLink")} testId="input-uw-walkthrough" />
            <Field label="GDrive Folder Link" value={f("gdriveFolderLink")} onChange={(v) => setField("gdriveFolderLink", v)} onBlur={() => saveField("gdriveFolderLink")} testId="input-uw-gdrive" />
            <Field label="SQFT" value={f("sqft")} onChange={(v) => setField("sqft", parseInt(v) || 0)} onBlur={() => saveField("sqft")} type="number" testId="input-uw-sqft" />
            <Field label="Garage SQFT" value={f("garageSqft")} onChange={(v) => setField("garageSqft", parseInt(v) || 0)} onBlur={() => saveField("garageSqft")} type="number" testId="input-uw-garage-sqft" />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-muted-foreground mb-1">Notes</label>
            <textarea
              className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={f("propertyNotes") ?? ""}
              onChange={(e) => setField("propertyNotes", e.target.value)}
              onBlur={() => saveField("propertyNotes")}
              placeholder="Enter notes about this property..."
              data-testid="input-uw-property-notes"
            />
          </div>
        </Section>

        <Section title="Rent Estimates">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="sm"
                disabled={!property?.address || rentEstimateMutation.isPending || !!(rentData && (rentData.rentEstimateLow || rentData.rentEstimateHigh || rentData.rentEstimateMedian))}
                onClick={() => {
                  if (property?.address) {
                    rentEstimateMutation.mutate(property.address);
                  }
                }}
                data-testid="button-pull-rents"
              >
                {rentEstimateMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fetching...</>
                ) : rentData && (rentData.rentEstimateLow || rentData.rentEstimateHigh || rentData.rentEstimateMedian) ? (
                  <><DollarSign className="h-4 w-4 mr-2" /> Rents Loaded</>
                ) : (
                  <><DollarSign className="h-4 w-4 mr-2" /> Pull Rents</>
                )}
              </Button>
              {!property?.address && (
                <span className="text-xs text-gray-500">Property must have an address to fetch rent estimates</span>
              )}
            </div>
            {rentData && (rentData.rentEstimateLow || rentData.rentEstimateHigh || rentData.rentEstimateMedian) ? (
              <div className="border rounded-lg p-4 bg-blue-50/50 dark:bg-blue-950/30 dark:border-blue-900/50">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-foreground mb-3">Rentcast Market Data</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-muted-foreground mb-1">Rent Estimate (Low)</label>
                    <div className="text-lg font-semibold text-gray-900 dark:text-foreground" data-testid="text-rent-low">
                      {rentData.rentEstimateLow ? `$${Number(rentData.rentEstimateLow).toLocaleString()}` : "—"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-muted-foreground mb-1">Rent Estimate (Median)</label>
                    <div className="text-lg font-semibold text-brand dark:text-blue-400" data-testid="text-rent-median">
                      {rentData.rentEstimateMedian ? `$${Number(rentData.rentEstimateMedian).toLocaleString()}` : "—"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-muted-foreground mb-1">Rent Estimate (High)</label>
                    <div className="text-lg font-semibold text-gray-900 dark:text-foreground" data-testid="text-rent-high">
                      {rentData.rentEstimateHigh ? `$${Number(rentData.rentEstimateHigh).toLocaleString()}` : "—"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-muted-foreground mb-1">Owner Name</label>
                    <div className="text-lg font-semibold text-gray-900 dark:text-foreground" data-testid="text-owner-name">
                      {rentData.ownerName || "—"}
                    </div>
                  </div>
                </div>
              </div>
            ) : rentData && !rentData.rentEstimateLow && !rentData.rentEstimateHigh && !rentData.rentEstimateMedian ? (
              <div className="border rounded-lg p-4 bg-yellow-50/50 dark:bg-yellow-950/30 dark:border-yellow-900/50">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">No rent estimate data available for this address.</p>
              </div>
            ) : null}
          </div>
        </Section>

        <Section title="Remodel / CapEx Calculator">
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <ReadOnlyField label="Room Additions Qty (auto)" value={String(calculations.roomAddQty)} testId="calc-uw-room-add-qty" />
              <Field label="Cost Each" value={f("roomAdditionsCost")} onChange={(v) => setField("roomAdditionsCost", v)} onBlur={() => saveField("roomAdditionsCost")} testId="input-uw-room-add-cost" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.roomAdditionsTotal)} testId="calc-room-add-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <ReadOnlyField label="Bath Additions Qty (auto)" value={String(calculations.bathAddQty)} testId="calc-uw-bath-add-qty" />
              <Field label="Cost Each" value={f("bathAdditionsCost")} onChange={(v) => setField("bathAdditionsCost", v)} onBlur={() => saveField("bathAdditionsCost")} testId="input-uw-bath-add-cost" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.bathAdditionsTotal)} testId="calc-bath-add-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="Kitchen Qty" value={f("kitchenQty")} onChange={(v) => setField("kitchenQty", parseInt(v) || 0)} onBlur={() => saveField("kitchenQty")} type="number" testId="input-uw-kitchen-qty" />
              <Field label="Amount" value={f("kitchenAmount")} onChange={(v) => setField("kitchenAmount", v)} onBlur={() => saveField("kitchenAmount")} testId="input-uw-kitchen-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.kitchenTotal)} testId="calc-kitchen-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="HVAC Qty" value={f("hvacQty")} onChange={(v) => setField("hvacQty", parseInt(v) || 0)} onBlur={() => saveField("hvacQty")} type="number" testId="input-uw-hvac-qty" />
              <Field label="Amount" value={f("hvacAmount")} onChange={(v) => setField("hvacAmount", v)} onBlur={() => saveField("hvacAmount")} testId="input-uw-hvac-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.hvacTotal)} testId="calc-hvac-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="Roof Qty" value={f("roofQty")} onChange={(v) => setField("roofQty", parseInt(v) || 0)} onBlur={() => saveField("roofQty")} type="number" testId="input-uw-roof-qty" />
              <Field label="Amount" value={f("roofAmount")} onChange={(v) => setField("roofAmount", v)} onBlur={() => saveField("roofAmount")} testId="input-uw-roof-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.roofTotal)} testId="calc-roof-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="Plumbing Qty" value={f("plumbingQty")} onChange={(v) => setField("plumbingQty", parseInt(v) || 0)} onBlur={() => saveField("plumbingQty")} type="number" testId="input-uw-plumbing-qty" />
              <Field label="Amount" value={f("plumbingAmount")} onChange={(v) => setField("plumbingAmount", v)} onBlur={() => saveField("plumbingAmount")} testId="input-uw-plumbing-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.plumbingTotal)} testId="calc-plumbing-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="Foundation Repair Qty" value={f("foundationRepairQty")} onChange={(v) => setField("foundationRepairQty", parseInt(v) || 0)} onBlur={() => saveField("foundationRepairQty")} type="number" testId="input-uw-foundation-qty" />
              <Field label="Amount" value={f("foundationRepairAmount")} onChange={(v) => setField("foundationRepairAmount", v)} onBlur={() => saveField("foundationRepairAmount")} testId="input-uw-foundation-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.foundationTotal)} testId="calc-foundation-total" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              <Field label="Other Qty" value={f("otherQty")} onChange={(v) => setField("otherQty", parseInt(v) || 0)} onBlur={() => saveField("otherQty")} type="number" testId="input-uw-other-qty" />
              <Field label="Amount" value={f("otherAmount")} onChange={(v) => setField("otherAmount", v)} onBlur={() => saveField("otherAmount")} testId="input-uw-other-amount" />
              <ReadOnlyField label="Total" value={fmtCurrency(calculations.otherTotal)} testId="calc-other-total" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
              <Field label="Additional Rehab % of PP" value={f("additionalRehabAmount")} onChange={(v) => setField("additionalRehabAmount", v)} onBlur={() => saveField("additionalRehabAmount")} testId="input-uw-additional-rehab" />
              <ReadOnlyField label="Rehab Amount" value={fmtCurrency(calculations.additionalRehab)} testId="calc-additional-rehab-amount" />
              <Field label="Buffer %" value={f("bufferPercent")} onChange={(v) => setField("bufferPercent", v)} onBlur={() => saveField("bufferPercent")} testId="input-uw-buffer-pct" />
              <ReadOnlyField label="Total CapEx" value={fmtCurrency(calculations.totalCapex)} testId="calc-total-capex" />
            </div>
          </div>
        </Section>

        <Section title="Cash Invested">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Home Purchase Price" value={f("homePurchasePrice")} onChange={(v) => setField("homePurchasePrice", v)} onBlur={() => saveField("homePurchasePrice")} testId="input-uw-purchase-price" />
            <Field label="Downpayment %" value={f("downpaymentPercent")} onChange={(v) => setField("downpaymentPercent", v)} onBlur={() => saveField("downpaymentPercent")} testId="input-uw-downpayment-pct" />
            <ReadOnlyField label="Downpayment $" value={fmtCurrency(calculations.downpayment)} testId="calc-downpayment" />
            <Field label="Closing Costs %" value={f("closingCostsPercent")} onChange={(v) => setField("closingCostsPercent", v)} onBlur={() => saveField("closingCostsPercent")} testId="input-uw-closing-pct" />
            <ReadOnlyField label="Closing Costs $" value={fmtCurrency(calculations.closingCosts)} testId="calc-closing-costs" />
            <Field label="Furnishings" value={f("furnishings")} onChange={(v) => setField("furnishings", v)} onBlur={() => saveField("furnishings")} testId="input-uw-furnishings" />
            <ReadOnlyField label="Improvements / CapEx" value={fmtCurrency(calculations.totalCapex)} testId="calc-capex-invested" />
            <ReadOnlyField
              label="Temp Holding Cost"
              value={fmtCurrency(calculations.tempHolding)}
              testId="calc-uw-holding-cost"
              tooltip={calculations.monthsUntilOcc > 0
                ? `Auto-calculated: (Mortgage P&I + Property Tax + Insurance + Sewer/Water + Gas + Electricity + Internet + HOA) × ${calculations.monthsUntilOcc} months`
                : "Set Months Until Occupied above 0 to auto-calculate holding costs. Currently using manual Temp Holding Cost value."}
            />
            <Field label="Deal Sponsor Fee" value={f("dealSponsorFee")} onChange={(v) => setField("dealSponsorFee", v)} onBlur={() => saveField("dealSponsorFee")} testId="input-uw-sponsor-fee" />
            <Field label="Invoice Authorization" value={f("invoiceAuthorization")} onChange={(v) => setField("invoiceAuthorization", v)} onBlur={() => saveField("invoiceAuthorization")} testId="input-uw-invoice-auth" />
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total Cash Invested</span>
              <span className="text-lg font-bold text-brand" data-testid="calc-total-cash-invested">{fmtCurrency(calculations.totalCashInvested)}</span>
            </div>
          </div>
        </Section>

        <Section title="Other Inputs">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Months Until Occupied" value={f("monthsUntilOccupied")} onChange={(v) => setField("monthsUntilOccupied", parseInt(v) || 0)} onBlur={() => saveField("monthsUntilOccupied")} type="number" testId="input-uw-months-occupied" />
            <Field label="Annual Interest Rate %" value={f("annualInterestRate")} onChange={(v) => setField("annualInterestRate", v)} onBlur={() => saveField("annualInterestRate")} testId="input-uw-interest-rate" />
            <Field label="Loan Term (Years)" value={f("loanTermYears")} onChange={(v) => setField("loanTermYears", parseInt(v) || 0)} onBlur={() => saveField("loanTermYears")} type="number" testId="input-uw-loan-term" />
            <Field label="Vacancy %" value={f("vacancyPercent")} onChange={(v) => setField("vacancyPercent", v)} onBlur={() => saveField("vacancyPercent")} testId="input-uw-vacancy-pct" />
            <Field label="Land Value % of PP" value={f("landValuePercent")} onChange={(v) => setField("landValuePercent", v)} onBlur={() => saveField("landValuePercent")} testId="input-uw-land-value-pct" />
            <ReadOnlyField label="Land Value $" value={fmtCurrency(calculations.landValueCalc)} testId="calc-land-value" tooltip={`Building Value (for depreciation) = Purchase Price − Land Value = ${fmtCurrency(calculations.buildingValue)}`} />
            <Field label="Annual Appreciation Avg %" value={f("annualAppreciationAvg")} onChange={(v) => setField("annualAppreciationAvg", v)} onBlur={() => saveField("annualAppreciationAvg")} testId="input-uw-appreciation" />
            <Field label="PadSplit Mgmt Fee %" value={f("padsplitManagementFeePercent")} onChange={(v) => setField("padsplitManagementFeePercent", v)} onBlur={() => saveField("padsplitManagementFeePercent")} testId="input-uw-padsplit-fee" />
            <ReadOnlyField label="PadSplit Mgmt Fee $" value={fmtCurrency(calculations.padsplitMgmtFee)} testId="calc-padsplit-fee" />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">PM Fee Type</label>
              <Select
                value={String(localFields.pmFeeType || "percent")}
                onValueChange={(v) => {
                  setField("pmFeeType", v);
                  setTimeout(() => saveField("pmFeeType"), 0);
                }}
              >
                <SelectTrigger className="text-sm" data-testid="select-pm-fee-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              label={String(localFields.pmFeeType) === "fixed" ? "PM Fee $" : "PM Fee %"}
              value={f("pmFeePercent")}
              onChange={(v) => setField("pmFeePercent", v)}
              onBlur={() => saveField("pmFeePercent")}
              testId="input-uw-pm-fee"
            />
            <ReadOnlyField label="PM Fee (Monthly)" value={fmtCurrency(calculations.pmFee)} testId="calc-pm-fee" />
          </div>
        </Section>

        <Section title="PadSplit Market Rates">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 space-y-1" data-testid="market-rates-instructions">
            <p className="font-medium">How to get market rates:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li>Open the PadSplit Market Insights link below in a new tab.</li>
              <li>Copy the shared-bath and private-bath <strong>weekly</strong> rates shown on that page and paste them into the fields below.</li>
              <li>Or click <strong>Auto-fill from city rates</strong> to instantly populate both fields using city-level averages we already have on file.</li>
            </ol>
            <p className="text-blue-600 text-xs mt-1">Note: some zip codes have no PadSplit listings and the insights page may appear empty. In that case, use the auto-fill button to fall back to city defaults.</p>
          </div>
          <div className="mb-4">
            <ReadOnlyField
              label="PadSplit Market Insights Link"
              value={f("zipCode") ? `https://www.padsplit.com/hosts/market-insights?zip=${f("zipCode")}` : "Enter zip code in Property Details to generate"}
              testId="calc-uw-zip-link"
            />
            {f("zipCode") && (
              <a
                href={`https://www.padsplit.com/hosts/market-insights?zip=${f("zipCode")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand hover:underline mt-1 inline-block"
                data-testid="link-padsplit-market"
              >
                Open in new tab <ExternalLink className="inline h-3 w-3 ml-0.5" />
              </a>
            )}
          </div>
          <div className="mb-4">
            {(() => {
              const cityLoading = !!parsedCity && cityRatesQuery.isLoading;
              const buttonDisabled = !parsedCity || cityLoading || !hasCityRates || cityRatesFilling;
              const buttonLabel = parsedCity ? `Auto-fill from ${parsedCity} city rates` : "Auto-fill from city rates";
              const hint = !parsedCity
                ? "Set a city on the property to enable auto-fill."
                : cityLoading
                  ? undefined
                  : cityRatesQuery.isError
                    ? `Couldn't load rates for ${parsedCity} — refresh the page to retry, or enter rates manually.`
                    : !hasCityRates
                      ? `No city-level rates on file for ${parsedCity}. Enter rates manually from the insights link above.`
                      : undefined;
              return (
                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={buttonDisabled}
                    data-testid="button-autofill-city-rates"
                    onClick={() => {
                      if (!parsedCity || !cityRatesRow) return;
                      setCityRatesFilling(true);
                      try {
                        const sharedWeekly = cityRatesRow.avgPriceShared ? String(parseFloat(String(cityRatesRow.avgPriceShared)).toFixed(2)) : null;
                        const privateWeekly = cityRatesRow.avgPricePrivate ? String(parseFloat(String(cityRatesRow.avgPricePrivate)).toFixed(2)) : null;
                        const sharedMonthly = sharedWeekly && parseFloat(sharedWeekly) > 0 ? (parseFloat(sharedWeekly) * 4.3).toFixed(2) : "";
                        const privateMonthly = privateWeekly && parseFloat(privateWeekly) > 0 ? (parseFloat(privateWeekly) * 4.3).toFixed(2) : "";
                        if (sharedWeekly) setField("furnishedSharedWeekly", sharedWeekly);
                        if (sharedMonthly) setField("furnishedSharedMonthly", sharedMonthly);
                        if (privateWeekly) setField("furnishedPrivateWeekly", privateWeekly);
                        if (privateMonthly) setField("furnishedPrivateMonthly", privateMonthly);
                        updateMutation.mutate({
                          ...(sharedWeekly ? { furnishedSharedWeekly: sharedWeekly, furnishedSharedMonthly: sharedMonthly } : {}),
                          ...(privateWeekly ? { furnishedPrivateWeekly: privateWeekly, furnishedPrivateMonthly: privateMonthly } : {}),
                        });
                        toast({ title: "Rates auto-filled", description: `Weekly rates populated from ${parsedCity} city averages. Monthly rates calculated at ×4.3.` });
                      } catch {
                        toast({ title: "Auto-fill failed", description: "Could not apply city rate data.", variant: "destructive" });
                      } finally {
                        setCityRatesFilling(false);
                      }
                    }}
                  >
                    {(cityLoading || cityRatesFilling) ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                    {buttonLabel}
                  </Button>
                  {hint && <span className="text-xs text-gray-500" data-testid="autofill-city-hint">{hint}</span>}
                </div>
              );
            })()}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">Shared Bath</h4>
              <Field label="Weekly Rate" value={f("furnishedSharedWeekly")} onChange={(v) => setField("furnishedSharedWeekly", v)} onBlur={() => {
                const weekly = n(f("furnishedSharedWeekly"));
                const monthly = weekly > 0 ? (weekly * 4.3).toFixed(2) : "";
                setField("furnishedSharedMonthly", monthly);
                updateMutation.mutate({ furnishedSharedWeekly: f("furnishedSharedWeekly"), furnishedSharedMonthly: monthly });
              }} testId="input-uw-shared-weekly" />
              <ReadOnlyField label="Monthly Rate" value={n(f("furnishedSharedMonthly")) > 0 ? fmtCurrency(n(f("furnishedSharedMonthly"))) : ""} testId="input-uw-shared-monthly" />
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">Private Bath</h4>
              <Field label="Weekly Rate" value={f("furnishedPrivateWeekly")} onChange={(v) => setField("furnishedPrivateWeekly", v)} onBlur={() => {
                const weekly = n(f("furnishedPrivateWeekly"));
                const monthly = weekly > 0 ? (weekly * 4.3).toFixed(2) : "";
                setField("furnishedPrivateMonthly", monthly);
                updateMutation.mutate({ furnishedPrivateWeekly: f("furnishedPrivateWeekly"), furnishedPrivateMonthly: monthly });
              }} testId="input-uw-private-weekly" />
              <ReadOnlyField label="Monthly Rate" value={n(f("furnishedPrivateMonthly")) > 0 ? fmtCurrency(n(f("furnishedPrivateMonthly"))) : ""} testId="input-uw-private-monthly" />
            </div>
          </div>
        </Section>

        <Section title="Monthly Income (Room-by-Room)">
          <div className="mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const totalRooms = Math.min(Number(localFields.futureBedrooms) || 0, MAX_ROOMS);
                const privateBathCount = Math.min(Number(localFields.privateBaths) || 0, totalRooms);
                const sharedCount = totalRooms - privateBathCount;
                const privateMonthly = f("furnishedPrivateMonthly");
                const sharedMonthly = f("furnishedSharedMonthly");
                if (!privateMonthly && !sharedMonthly) {
                  toast({ title: "Set market rates first", description: "Enter weekly rates in PadSplit Market Rates section above.", variant: "destructive" });
                  return;
                }
                const buildRooms = (existing: RoomData[]): RoomData[] => {
                  const updated: RoomData[] = [];
                  for (let i = 0; i < totalRooms; i++) {
                    const isPrivate = i < privateBathCount;
                    updated.push({
                      ...(existing[i] || {}),
                      rent: isPrivate ? privateMonthly : sharedMonthly,
                      furnished: true,
                      privateBath: isPrivate,
                    });
                  }
                  return updated;
                };
                setLocalFields((prev) => {
                  const existing = (prev.roomsData as RoomData[] | null) || [];
                  return { ...prev, roomsData: buildRooms(existing) };
                });
                setTimeout(() => {
                  const existing = (localFields.roomsData as RoomData[] | null) || [];
                  updateMutation.mutate({ roomsData: buildRooms(existing) });
                }, 100);
                toast({ title: "Rooms auto-filled", description: `${privateBathCount} private bath room${privateBathCount !== 1 ? "s" : ""}, ${sharedCount} shared bath room${sharedCount !== 1 ? "s" : ""}.` });
              }}
              data-testid="button-autofill-rooms"
            >
              Auto-Fill from Market Rates
            </Button>
          </div>
          <div className="space-y-3">
            {Array.from({ length: Math.max(futureBedrooms, 1) }, (_, i) => {
              const room = roomsData[i] || {};
              return (
                <div key={i} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end p-3 bg-gray-50 rounded-lg" data-testid={`room-income-row-${i}`}>
                  <div className="col-span-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Room {i + 1} Rent</label>
                    <Input
                      value={room.rent || ""}
                      onChange={(e) => setRoomField(i, "rent", e.target.value)}
                      onBlur={saveRoomsData}
                      className="text-sm"
                      data-testid={`input-room-rent-${i}`}
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                    <Input
                      value={room.notes || ""}
                      onChange={(e) => setRoomField(i, "notes", e.target.value)}
                      onBlur={saveRoomsData}
                      className="text-sm"
                      data-testid={`input-room-notes-${i}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={room.furnished || false}
                      onCheckedChange={(v) => { setRoomField(i, "furnished", v); setTimeout(saveRoomsData, 0); }}
                      data-testid={`switch-room-furnished-${i}`}
                    />
                    <span className="text-xs text-gray-600">Furnished</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={room.privateEntrance || false}
                      onCheckedChange={(v) => { setRoomField(i, "privateEntrance", v); setTimeout(saveRoomsData, 0); }}
                      data-testid={`switch-room-private-entrance-${i}`}
                    />
                    <span className="text-xs text-gray-600">Pvt Entry</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={room.privateBath || false}
                      onCheckedChange={(v) => { setRoomField(i, "privateBath", v); setTimeout(saveRoomsData, 0); }}
                      data-testid={`switch-room-private-bath-${i}`}
                    />
                    <span className="text-xs text-gray-600">Pvt Bath</span>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Additions $</label>
                    <Input
                      value={room.additions || ""}
                      onChange={(e) => setRoomField(i, "additions", e.target.value)}
                      onBlur={saveRoomsData}
                      className="text-sm"
                      data-testid={`input-room-additions-${i}`}
                    />
                  </div>
                  <ReadOnlyField label="Total" value={fmtCurrency(n(room.rent) + n(room.additions))} testId={`calc-room-total-${i}`} />
                </div>
              );
            })}
          </div>
          <div className="mt-4 p-3 bg-green-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total Monthly Gross Income</span>
              <span className="text-lg font-bold text-green-700" data-testid="calc-total-monthly-income">{fmtCurrency(calculations.totalMonthlyIncome)}</span>
            </div>
          </div>
        </Section>

        <Section title="Monthly Expenses">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Field label="Sewer & Water" value={f("sewerWater")} onChange={(v) => setField("sewerWater", v)} onBlur={() => saveField("sewerWater")} testId="input-uw-sewer" />
            <Field label="Gas" value={f("gas")} onChange={(v) => setField("gas", v)} onBlur={() => saveField("gas")} testId="input-uw-gas" />
            <ReadOnlyField label={`Electricity ($35 × ${futureBedrooms} rooms)`} value={fmtCurrency(calculations.electricityCalc)} testId="calc-electricity" />
            <Field label="Internet" value={f("internet")} onChange={(v) => setField("internet", v)} onBlur={() => saveField("internet")} testId="input-uw-internet" />
            <Field label="Lawn Care" value={f("lawnCare")} onChange={(v) => setField("lawnCare", v)} onBlur={() => saveField("lawnCare")} testId="input-uw-lawn" />
            <Field label="Cleaning" value={f("cleaning")} onChange={(v) => setField("cleaning", v)} onBlur={() => saveField("cleaning")} testId="input-uw-cleaning" />
            <Field label="Capital Improvements Budget" value={f("capitalImprovementsBudget")} onChange={(v) => setField("capitalImprovementsBudget", v)} onBlur={() => saveField("capitalImprovementsBudget")} testId="input-uw-cap-improve" />
            <Field label="Misc Repairs" value={f("miscRepairs")} onChange={(v) => setField("miscRepairs", v)} onBlur={() => saveField("miscRepairs")} testId="input-uw-misc-repairs" />
            <Field label="HOA" value={f("hoa")} onChange={(v) => setField("hoa", v)} onBlur={() => saveField("hoa")} testId="input-uw-hoa" />
            <Field label="Annual Property Tax % of PP" value={f("annualPropertyTaxPercent")} onChange={(v) => setField("annualPropertyTaxPercent", v)} onBlur={() => saveField("annualPropertyTaxPercent")} testId="input-uw-property-tax-pct" />
            <ReadOnlyField label="Annual Property Tax $" value={fmtCurrency(calculations.annualPropertyTaxCalc)} testId="calc-annual-property-tax" />
            <ReadOnlyField label="Monthly Property Tax" value={fmtCurrency(calculations.monthlyPropertyTax)} testId="calc-monthly-tax" />
            <Field label="Annual Insurance % of PP" value={f("annualInsurancePercent")} onChange={(v) => setField("annualInsurancePercent", v)} onBlur={() => saveField("annualInsurancePercent")} testId="input-uw-insurance-pct" />
            <ReadOnlyField label="Annual Insurance $" value={fmtCurrency(calculations.annualInsuranceCalc)} testId="calc-annual-insurance" />
            <ReadOnlyField label="Monthly Insurance" value={fmtCurrency(calculations.monthlyInsurance)} testId="calc-monthly-insurance" />
            <ReadOnlyField label="Mortgage P&I" value={fmtCurrency(calculations.mortgagePI)} testId="calc-mortgage" tooltip={`Monthly payment on a ${fmtCurrency(calculations.loanAmount)} loan at ${f("annualInterestRate")}% for ${f("loanTermYears")} years.\nFormula: P × [r(1+r)^n] / [(1+r)^n − 1]`} />
            <ReadOnlyField label="PadSplit Mgmt Fee" value={fmtCurrency(calculations.padsplitMgmtFee)} testId="calc-mgmt-fee-expense" />
            <ReadOnlyField label="PM Fee" value={fmtCurrency(calculations.pmFee)} testId="calc-pm-fee-expense" />
            <ReadOnlyField label="Vacancy" value={fmtCurrency(calculations.vacancyAmount)} testId="calc-vacancy" />
          </div>
          <div className="mt-4 p-3 bg-red-50 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-gray-900">Total Monthly Expenses</span>
              <span className="text-lg font-bold text-red-700" data-testid="calc-total-monthly-expenses">{fmtCurrency(calculations.totalMonthlyExpenses)}</span>
            </div>
          </div>
        </Section>

        <Section title="Key Metrics">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Cost Per Room</p>
              <p className="text-2xl font-bold text-brand" data-testid="calc-cost-per-room">{fmtCurrency(calculations.costPerRoom)}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Cash-on-Cash Return</p>
              <p className="text-2xl font-bold text-green-700" data-testid="calc-cash-on-cash">{fmtPct(calculations.cashOnCash)}</p>
            </div>
            <div className={`p-4 rounded-lg text-center ${calculations.monthlyCashflow >= 0 ? "bg-green-50" : "bg-red-50"}`}>
              <p className="text-sm text-gray-600 mb-1">Monthly Cashflow</p>
              <p className={`text-2xl font-bold ${calculations.monthlyCashflow >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="calc-monthly-cashflow">{fmtCurrency(calculations.monthlyCashflow)}</p>
            </div>
          </div>
        </Section>

        <Section title="Depreciation Schedule" defaultOpen={false}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ReadOnlyField
              label="Annual Depreciation (27.5yr)"
              value={fmtCurrency(calculations.annualDepreciation)}
              testId="calc-annual-depreciation"
              tooltip={`Straight-line depreciation over 27.5 years.\nDepreciable Basis = Building Value + Total CapEx = ${fmtCurrency(calculations.depreciableBasis)}\nAnnual Deduction = Depreciable Basis ÷ 27.5`}
            />
            <ReadOnlyField
              label="Cost Seg Year 1 (30% @ 100% bonus)"
              value={fmtCurrency(calculations.costSegYear1)}
              testId="calc-cost-seg-year1"
              tooltip={`Cost segregation accelerated depreciation.\nDepreciable Basis = Building Value + Total CapEx = ${fmtCurrency(calculations.depreciableBasis)}\nYear 1 = 30% of Depreciable Basis (${fmtCurrency(calculations.depreciableBasis)}) taken at 100% bonus depreciation.`}
            />
            <ReadOnlyField
              label="Cost Seg Remaining Annual"
              value={fmtCurrency(calculations.costSegRemaining)}
              testId="calc-cost-seg-remaining"
              tooltip={`Remaining depreciable basis after Year 1 cost seg, depreciated over 27.5 years.\nDepreciable Basis = Building Value + Total CapEx\n(Depreciable Basis − Year 1 Amount) ÷ 27.5`}
            />
          </div>
        </Section>

        <Section title="CAP Rate Calculator" defaultOpen={false}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <ReadOnlyField
              label="Monthly NOI"
              value={fmtCurrency(calculations.monthlyNOI)}
              testId="calc-monthly-noi"
              tooltip={`Net Operating Income = Gross Income − Operating Expenses (excluding mortgage).\nAnnual NOI = ${fmtCurrency(calculations.annualNOI)}`}
            />
            <ReadOnlyField label="Value @ 6 CAP" value={fmtCurrency(calculations.capRate6)} testId="calc-cap-6" tooltip={`Property value = Annual NOI ÷ Cap Rate\n${fmtCurrency(calculations.annualNOI)} ÷ 0.06`} />
            <ReadOnlyField label="Value @ 7 CAP" value={fmtCurrency(calculations.capRate7)} testId="calc-cap-7" tooltip={`Property value = Annual NOI ÷ Cap Rate\n${fmtCurrency(calculations.annualNOI)} ÷ 0.07`} />
            <ReadOnlyField label="Value @ 8 CAP" value={fmtCurrency(calculations.capRate8)} testId="calc-cap-8" tooltip={`Property value = Annual NOI ÷ Cap Rate\n${fmtCurrency(calculations.annualNOI)} ÷ 0.08`} />
            <ReadOnlyField label="Value @ 10 CAP" value={fmtCurrency(calculations.capRate10)} testId="calc-cap-10" tooltip={`Property value = Annual NOI ÷ Cap Rate\n${fmtCurrency(calculations.annualNOI)} ÷ 0.10`} />
          </div>
        </Section>
      </div>
    </AppLayout>
  );
}
